/**
 * Wall-port pill geometry — the single source both the space layout and the
 * app-shell placement read.
 *
 *   - `placeSpace` (layout.ts) RESERVES a wall lane of `PORT_LANE` px on any
 *     side that carries ports, so the pills get their own margin and never
 *     overlap the content items.
 *   - `resolvePortPlacements` (portPlacement.ts) POSITIONS the pills inside
 *     that lane, importing the constants below directly.
 *
 * `PORT_LANE` must stay ≥ `PORT_WALL_GAP + PORT_W + PORT_GAP`, or a wall of
 * pills overflows the lane the layout reserved for it.
 */
export const PORT_W = 150;
export const PORT_H = 28;
/** Gap between the space wall (carpet edge) and the pill's outer edge — the
 *  pill's OUTER breather. Wide enough that a wall of pills reads as sitting
 *  INSIDE the carpet with room to the rounded border, not jammed against it.
 *  (The pill's node-side gap is the content PADDING + PORT_GAP.) */
export const PORT_WALL_GAP = 24;
/** Vertical gap between stacked pills on the same wall. */
export const PORT_GAP = 8;
/** Horizontal room a wall of pills needs BEYOND the normal content padding —
 *  the wall gap, the pill itself, and a trailing breather. `placeSpace` adds
 *  this to a side's padding whenever that side carries ports. */
export const PORT_LANE = PORT_WALL_GAP + PORT_W + PORT_GAP;

/**
 * Natural (pre-clamp) vertical extent of one wall's pill stack, in pill-CENTRE
 * coordinates. `owners` = every porting node on this wall as its centre-y and
 * its pill count.
 *
 * MIRRORS `resolvePortPlacements` (portPlacement.ts): centre each
 * owner's stack on its centre-y, flatten, then down-only de-overlap at a
 * `PORT_H + PORT_GAP` pitch. `placeSpace` uses this to grow the carpet so it
 * CONTAINS the natural stack — the pill placement then never has to compress
 * the stack against the floor (the "carpet doesn't respect ports" bug).
 * Returns null when the wall carries no ports.
 */
export function wallStackCentreExtent(
  owners: readonly { readonly cy: number; readonly count: number }[],
): { readonly min: number; readonly max: number } | null {
  const centres: number[] = [];
  for (const o of owners) {
    const stackH = o.count * PORT_H + (o.count - 1) * PORT_GAP;
    const first = o.cy - stackH / 2 + PORT_H / 2;
    for (let i = 0; i < o.count; i++) centres.push(first + i * (PORT_H + PORT_GAP));
  }
  if (centres.length === 0) return null;
  centres.sort((a, b) => a - b);
  const pitch = PORT_H + PORT_GAP;
  for (let i = 1; i < centres.length; i++) {
    if (centres[i]! < centres[i - 1]! + pitch) centres[i] = centres[i - 1]! + pitch;
  }
  return { min: centres[0]!, max: centres[centres.length - 1]! };
}
